import {connect} from 'react-redux';
import {useEffect, useMemo, useState} from 'react';

import SessionTransforms from './session-transforms.js';
import pluckQueryParam from '@explorer/util/pluck-query-param';
import {schema as SessionSchema} from './session-schema.js';
import {buildActionsFromHash} from './builders/hash.js';
import {buildActionsFromStoredState} from './builders/stored-state.js';
import {fetchFromBlobz} from './api.js';
import {
  fetchState,
  getObjectValue,
  getPropName,
  lintState,
  saveState,
} from './util.js';
import {notify} from '@explorer/actions/notifications';
import {setObjectValue} from './common.js';
import {setSaveErrors} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';

const identityFn = v => v;

const updateSessionStorage = flatProps => {
  const sessionObject = {};
  for (let i = 0, ii = flatProps.length; i < ii; i += 2) {
    const key = flatProps[i];
    const value = flatProps[i + 1];
    const transformFn = SessionTransforms.transforms[key] ?? identityFn;
    setObjectValue(sessionObject, key, transformFn(value));
  }

  return lintState(sessionObject).then(lintErrors => {
    if (lintErrors.length === 0) {
      saveState(sessionObject);
    }
    return lintErrors;
  });
};

export const SessionStorageComponent = ({
  children,
  dispatch,
  onReadyChange,
  seriesLoading,
  ...props
}) => {
  const [isLoading, setLoading] = useState(true);
  const [loadingPromise, setLoadingPromise] = useState(null);

  useEffect(() => {
    if (loadingPromise) {
      loadingPromise.then(actions => {
        actions.forEach(action => dispatch(action));
        setLoading(false);
        onReadyChange(true);
      });
    }
  }, [loadingPromise, dispatch, onReadyChange]);

  useEffect(() => {
    if (!loadingPromise && !seriesLoading) {
      const sessionParam = pluckQueryParam('s');
      if (sessionParam) {
        // assume s= is the session ID
        const recallSessionId = sessionParam;
        // reset the URL
        setLoadingPromise(
          fetchFromBlobz(recallSessionId)
            .then(r => r.data)
            .then(storedState => {
              trackEvent('blobz:load:success', {
                sessionId: recallSessionId,
              });
              return buildActionsFromStoredState(storedState);
            })
            .catch(() => {
              trackEvent('blobz:load:failure', {
                sessionId: recallSessionId,
              });

              dispatch(
                notify({
                  severity: 'error',
                  closeButton: true,
                  persist: true,
                  message: {i18nKey: 'shareDialog.errorLoad'},
                  key: 'errorLoadingBlobz',
                  'data-qe': 'loading-blobz-load-error',
                })
              );

              // if there is a 404 or other error, resolve with empty actions list
              return [];
            })
        );
      } else if (location.hash) {
        setLoadingPromise(
          new Promise(resolve => {
            trackEvent('blobz:load:from-hash', {
              data: location.hash,
            });
            const actions = buildActionsFromHash(location.hash);
            // clear out the hash
            location.hash = '';
            resolve(actions);
          })
        );
      } else {
        setLoadingPromise(
          fetchState().then(st => buildActionsFromStoredState(st))
        );
      }
    }
  }, [
    dispatch,
    isLoading,
    setLoading,
    onReadyChange,
    loadingPromise,
    seriesLoading,
  ]);

  const flatProps = useMemo(() => {
    const arr = [];
    Object.keys(SessionSchema).forEach(key => {
      arr.push(key);
      arr.push(props[getPropName(key)]);
    });
    return arr;
  }, [props]);

  useEffect(() => {
    if (!isLoading) {
      updateSessionStorage(flatProps).then(errors => {
        if (errors.length > 0) {
          // for the cases in which this happens,
          //  having this logged is important.
          // eslint-disable-next-line no-console
          console.warn('Failed to save user state!', errors);
        }
        dispatch(setSaveErrors(errors));
      });
    }
  }, [dispatch, isLoading].concat(flatProps)); // eslint-disable-line react-hooks/exhaustive-deps

  return children;
};

const mapStateToProps = state => {
  const mappedState = {
    seriesLoading: state.series.loading,
  };

  for (const key in SessionSchema) {
    const value = SessionTransforms.selectors[key]
      ? SessionTransforms.selectors[key](state)
      : getObjectValue(state, key);
    mappedState[getPropName(key)] = value;
  }

  return mappedState;
};

export default connect(mapStateToProps)(SessionStorageComponent);
